import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export default function FeedbackDetailsModal({ pair, onClose }) {
  const [feedback, setFeedback] = useState({ mentor: null, mentee: null });
  const { t } = useTranslation();

  function parseIfString(input) {
    if (!input) return {};
    if (typeof input === 'string') {
      try {
        return JSON.parse(input);
      } catch (e) {
        console.warn('⚠️ JSON-Parsing fehlgeschlagen:', input);
        return {};
      }
    }
    return input;
  }

  useEffect(() => {
    if (!pair || typeof pair !== 'object') return;

    async function fetchFeedback() {
      try {
        const res = await axios.get('/mentoring/feedback/details', {
          params: {
            mentee_user_id: pair.mentee_user_id,
            mentor_user_id: pair.mentor_user_id
          },
          withCredentials: true
        });

        const entries = res.data || [];

        const menteeFeedback = entries
          .filter(e => e.role === 'mentee')
          .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))[0] || null;

        const mentorFeedback = entries
          .filter(e => e.role === 'mentor')
          .sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))[0] || null;

        if (menteeFeedback) {
          menteeFeedback.goals = parseIfString(menteeFeedback.goals);
          menteeFeedback.competences = parseIfString(menteeFeedback.competences);
        }

        if (mentorFeedback) {
          mentorFeedback.criteria = parseIfString(mentorFeedback.criteria);
        }

        setFeedback({ mentee: menteeFeedback, mentor: mentorFeedback });

      } catch (err) {
        console.error('Fehler beim Laden des Feedbacks:', err);
      }
    }

    fetchFeedback();
  }, [pair]);

  const mentee = feedback.mentee?.user || {};
  const mentor = feedback.mentor?.user || {};

  function renderChips(obj) {
    if (!obj || Object.keys(obj).length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-1">
        {Object.entries(obj).map(([key, value], idx) => (
          <span key={idx} className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
            {key}: {value}
          </span>
        ))}
      </div>
    );
  }

  function ScoreCircle({ value }) {
    const active = '#4ade80';
    const inactive = '#d1d5db';
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(i => (
          <div
            key={i}
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: i <= value ? active : inactive }}
          ></div>
        ))}
      </div>
    );
  }

  if (!feedback.mentee && !feedback.mentor) {
    return (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center">
        <div className="bg-white text-black rounded-xl shadow-lg w-full max-w-xl p-6 text-center">
          <h2 className="text-xl font-semibold mb-4">{t('feedbackDetails')}</h2>
          <p className="text-gray-700">{t('noFeedbackYet')}</p>
          <button onClick={onClose} className="mt-6 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
            {t('close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center">
      <div className="bg-white text-black rounded-xl shadow-lg w-full max-w-4xl p-6 overflow-y-auto max-h-[90vh]">
        {/* Kopfbereich */}
        <div className="flex justify-between items-center border-b pb-2">
          <h2 className="text-xl font-bold">{t('feedbackDetails')}</h2>
          <button onClick={onClose} className="text-red-500 font-bold" aria-label={t('close')}>✕</button>
        </div>

        <div className="flex justify-between items-center mt-4">
          {/* Mentee */}
          <div className="flex gap-4 items-center">
            <img
              src={pair?.mentee_avatar_path
                ? `/mentoring/uploads/${pair.mentee_avatar_path}`
                : '/mentoring/uploads/default-avatar.png'}
              alt="mentee"
              className="w-10 h-10 rounded-full"
              onError={(e) => { e.currentTarget.src = '/mentoring/uploads/default-avatar.png'; }}
            />
            <div>
              <div className="font-semibold">{mentee.first_name} {mentee.last_name}</div>
              <div className="text-sm text-gray-500">{mentee.abteilung} · {mentee.job_title}</div>
            </div>
          </div>

          {/* Meilenstein */}
          <div className="text-center">
            <div className="font-medium">{feedback.mentee?.milestone || feedback.mentor?.milestone || '–'}</div>
            <div className="text-xs text-gray-500">
              {t('mentee')}: {feedback.mentee?.submitted_at?.slice(0, 10) || '–'}<br />
              {t('mentor')}: {feedback.mentor?.submitted_at?.slice(0, 10) || '–'}
            </div>
          </div>

          {/* Mentor */}
          <div className="flex gap-4 items-center">
           <img
              src={pair?.mentor_avatar_path
                ? `/mentoring/uploads/${pair.mentor_avatar_path}`
                : '/mentoring/uploads/default-avatar.png'}
              alt="mentor"
              className="w-10 h-10 rounded-full"
              onError={(e) => { e.currentTarget.src = '/mentoring/uploads/default-avatar.png'; }}
            />
            <div>
              <div className="font-semibold">{mentor.first_name} {mentor.last_name}</div>
              <div className="text-sm text-gray-500">{mentor.abteilung} · {mentor.job_title}</div>
            </div>
          </div>
        </div>

        {/* Bewertung */}
        <div className="mt-6">
          <h3 className="font-semibold mb-2">{t('satisfaction')}</h3>
          <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
            <div>
              <div className="mb-1 font-medium text-black">{t('mentee')}</div>
              <div className="mb-1">{t('program')}</div>
              <ScoreCircle value={feedback.mentee?.satisfaction_score || 0} />
            </div>
            <div>
              <div className="mb-1 font-medium text-black">{t('mentor')}</div>
              <div className="mb-1">{t('program')}</div>
              <ScoreCircle value={feedback.mentor?.satisfaction_score || 0} />
            </div>
            <div>
              <div className="mb-1 font-medium text-black">{t('comparison')}</div>
              <div className="mb-1">{t('overallAverage')}</div>
              <ScoreCircle value={Math.round(
                ((feedback.mentee?.satisfaction_score || 0) +
                  (feedback.mentor?.satisfaction_score || 0)) / 2
              )} />
            </div>
          </div>
        </div>

        {/* Freitext */}
        <div className="mt-6 grid grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold mb-1">{t('menteeComment')}</h4>
            <p className="text-sm text-gray-700 whitespace-pre-line">{feedback.mentee?.comments || '–'}</p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">{t('mentorComment')}</h4>
            <p className="text-sm text-gray-700 whitespace-pre-line">{feedback.mentor?.comments || '–'}</p>
          </div>
        </div>

        {/* Themen-Fokus */}
        <div className="mt-6">
          <h3 className="font-semibold mb-2">{t('topicFocus')}</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium">{t('goalsMentee')}</h4>
              {renderChips(feedback.mentee?.goals)}
            </div>
            <div>
              <h4 className="text-sm font-medium">{t('competencesMentee')}</h4>
              {renderChips(feedback.mentee?.competences)}
            </div>
            <div>
              <h4 className="text-sm font-medium">{t('criteriaMentor')}</h4>
              {renderChips(feedback.mentor?.criteria)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
